import React, { useState } from 'react';
import { WEB3FORMS_ACCESS_KEY } from '../../config/web3forms';

// ------------------------------------------------------------------
// Low-Voltage Project Request — structured intake for a design + install
// proposal. Runs client-side; submits (with file attachments) to Web3Forms.
// ------------------------------------------------------------------

const FALLBACK_EMAIL = 'info@buildlabconsulting.com';
const MAX_FILE_MB = 10;

const NAVY = '#020842', BLUE = '#0044EB', PINK = '#F44380', INK = '#16203C',
    SLATE = '#54648A', LINE = '#D7E2F1', AMBER = '#B45309', AMBER_BG = '#FFF7ED', AMBER_LINE = '#F3C88C';

export const PROJECT_TYPES = ['New construction', 'Renovation / retrofit', 'Tenant improvement'];
// 'Residential — single-family' added 2026-07-30 (real staff request: a
// Headend Install Labor rate distinct from Multifamily's needed a real
// building type to key off — this app's whole domain was multifamily/
// affordable-housing low-voltage design until then, with no single-family
// category at all (see the Video Doorbell camera_subtype's own comment for
// the earlier residential-surveillance-only use case that first surfaced
// this gap).
export const BUILDING_TYPES = ['Multifamily — market rate', 'Residential — single-family', 'Affordable housing (LIHTC)', 'Mixed-income', 'Senior / supportive housing', 'Mixed-use', 'Commercial', 'Other'];
export const SYSTEMS = [
    'Structured cabling (data/voice)', 'Data/Communications', 'CCTV / video surveillance',
    'Access control & gate/door entry', 'DAS / ERRCS (public safety radio)',
    'Area of refuge (CBC 1009)', 'IT closet / MDF-IDF build-out', 'Not sure yet — need a recommendation',
];
const ISP_STATUS = ['Not selected yet', 'In negotiation', 'Already signed'];
const YES_NO = ['Yes', 'No'];
const YES_NO_UNSURE = ['Yes', 'No', 'Not sure'];
const BUDGET_RANGES = ['Not sure yet', 'Under $2,500/unit', '$2,500–$5,000/unit', '$5,000–$8,000/unit', '$8,000+/unit'];

const css = `
.lvr *{box-sizing:border-box}
.lvr{background:#fff;border:1px solid ${LINE};border-radius:20px;padding:clamp(20px,4vw,40px);box-shadow:0 30px 70px rgba(6,16,54,.08)}
.lvr__sec{padding:22px 0;border-top:1px solid #EEF2F8}
.lvr__sec:first-child{border-top:none;padding-top:4px}
.lvr__sh{display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;margin-bottom:4px}
.lvr__st{font-family:'Axiforma Bold',sans-serif;color:${NAVY};font-size:clamp(18px,2.4vw,21px)}
.lvr__sn{font-family:'Axiforma Regular',sans-serif;color:${SLATE};font-size:13.5px}
.lvr__hint{font-family:'Axiforma Regular',sans-serif;color:${SLATE};font-size:14px;margin:2px 0 16px}
.lvr__grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.lvr__f{margin-bottom:13px}
.lvr__f label{display:block;font-family:'Axiforma SemiBold',sans-serif;color:${INK};font-size:13.5px;margin-bottom:7px}

/* text inputs — filled, soft, lifts on focus */
.lvr__f input[type=text],.lvr__f input[type=email],.lvr__f input[type=tel],.lvr__f textarea{
  width:100%;border:1.5px solid ${LINE};border-radius:12px;padding:12px 14px;
  font-family:'Axiforma Regular',sans-serif;font-size:15px;color:${INK};background:#F7FAFD;
  transition:border-color .15s,box-shadow .15s,background-color .15s;}
.lvr__f input::placeholder,.lvr__f textarea::placeholder{color:#9AA7C2}
.lvr__f input:hover:not(:focus),.lvr__f textarea:hover:not(:focus){border-color:#B9CBEA}
.lvr__f input:focus,.lvr__f textarea:focus{outline:none;border-color:${BLUE};background:#fff;box-shadow:0 0 0 4px rgba(0,68,235,.10)}
.lvr__req{color:${PINK};margin-left:2px}

/* flowing multi/long option chips */
.lvr__opts{display:flex;flex-wrap:wrap;gap:9px}
.lvr__chip{background:#F7FAFD;border:1.5px solid ${LINE};border-radius:999px;padding:10px 16px;cursor:pointer;font-family:'Axiforma SemiBold',sans-serif;color:${INK};font-size:13.5px;transition:.15s}
.lvr__chip:hover{border-color:#B9CBEA;background:#fff}
.lvr__chip[aria-pressed=true]{border-color:${BLUE};background:${BLUE};color:#fff}

/* segmented control — compact joined toggle for short single-select choices */
.lvr__seg{display:inline-flex;flex-wrap:wrap;background:#F1F5FB;border:1.5px solid ${LINE};border-radius:13px;padding:4px;gap:3px}
.lvr__segbtn{border:none;background:transparent;border-radius:9px;padding:9px 17px;font-family:'Axiforma SemiBold',sans-serif;font-size:13.5px;color:${SLATE};cursor:pointer;transition:.15s;white-space:nowrap}
.lvr__segbtn:hover:not([aria-pressed=true]){color:${INK}}
.lvr__segbtn[aria-pressed=true]{background:#fff;color:${BLUE};box-shadow:0 3px 10px rgba(6,16,54,.12)}

/* custom file upload — replaces the native "Choose File" control */
.lvr__upload{border:1.5px dashed ${LINE};border-radius:14px;padding:14px;background:#FAFCFF;transition:border-color .15s}
.lvr__upload:hover{border-color:#B9CBEA}
.lvr__uploadbtn{display:inline-flex;align-items:center;gap:9px;background:#fff;border:1.5px solid ${LINE};border-radius:11px;padding:11px 19px;cursor:pointer;font-family:'Axiforma SemiBold',sans-serif;color:${BLUE};font-size:14px;transition:.15s}
.lvr__uploadbtn:hover{border-color:${BLUE};background:#F3F7FF}
.lvr__uploadinput{position:absolute;width:1px;height:1px;opacity:0;overflow:hidden}
.lvr__filechip{display:flex;align-items:center;gap:12px;background:#fff;border:1.5px solid ${LINE};border-radius:12px;padding:10px 12px}
.lvr__fileicon{flex:none;width:36px;height:36px;border-radius:9px;background:#E3F0FF;display:flex;align-items:center;justify-content:center}
.lvr__filemeta{flex:1;min-width:0}
.lvr__filename{font-family:'Axiforma SemiBold',sans-serif;color:${INK};font-size:13.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.lvr__filesize{font-family:'Axiforma Regular',sans-serif;color:${SLATE};font-size:12px;margin-top:2px}
.lvr__fileremove{flex:none;width:28px;height:28px;border-radius:9px;border:none;background:#F1F5FB;color:${SLATE};font-size:17px;cursor:pointer;line-height:1;display:flex;align-items:center;justify-content:center;transition:.15s}
.lvr__fileremove:hover{background:#FDE7E7;color:#DC2626}
.lvr__filenote{font-family:'Axiforma Regular',sans-serif;color:${SLATE};font-size:12.5px;margin-top:8px}

.lvr__warn{background:${AMBER_BG};border:1.5px solid ${AMBER_LINE};border-radius:16px;padding:20px 22px;margin:6px 0 20px;display:flex;gap:14px}
.lvr__warnicon{flex:none;width:38px;height:38px;border-radius:10px;background:${AMBER};display:flex;align-items:center;justify-content:center}
.lvr__warnt{font-family:'Axiforma Bold',sans-serif;color:#7C3A0A;font-size:17px;margin:0 0 6px}
.lvr__warnd{font-family:'Axiforma Regular',sans-serif;color:#7C3A0A;font-size:14.5px;line-height:1.55;margin:0}
.lvr__btn{display:inline-flex;align-items:center;justify-content:center;background:${BLUE};color:#fff;border:none;border-radius:12px;padding:16px 28px;font-family:'Axiforma Bold',sans-serif;font-size:16px;cursor:pointer;transition:.15s;width:100%}
.lvr__btn:hover{background:#0037c4}
.lvr__btn:disabled{opacity:.55;cursor:not-allowed}
.lvr__msg{font-family:'Axiforma Regular',sans-serif;font-size:14px;margin-top:12px;line-height:1.5;color:${SLATE}}
.lvr__disc{text-align:center;font-family:'Axiforma Regular',sans-serif;color:${SLATE};font-size:12.5px;margin-top:12px}
.lvr__done{text-align:center;padding:40px 10px}
.lvr__done h3{font-family:'Axiforma Bold',sans-serif;color:${NAVY};font-size:28px;margin:16px 0 10px}
.lvr__done p{font-family:'Axiforma Regular',sans-serif;color:${SLATE};font-size:16px;line-height:1.6;max-width:520px;margin:0 auto}
@media(max-width:760px){.lvr__grid{grid-template-columns:1fr}}
`;

const Field = ({ label, required, children }) => (
    <div className="lvr__f">
        <label>{label}{required && <span className="lvr__req">*</span>}</label>
        {children}
    </div>
);

// Flowing pills — for multi-select, or single-select lists too long/varied to join into a bar.
const Chips = ({ options, value, onToggle, multi }) => (
    <div className="lvr__opts">
        {options.map((o) => (
            <button type="button" key={o} className="lvr__chip"
                aria-pressed={multi ? value.includes(o) : value === o}
                onClick={() => onToggle(o)}>{o}</button>
        ))}
    </div>
);

// Joined toggle bar — for short (2-3 option) single-select choices like Yes/No.
const Segmented = ({ options, value, onChange }) => (
    <div className="lvr__seg" role="group">
        {options.map((o) => (
            <button type="button" key={o} className="lvr__segbtn"
                aria-pressed={value === o}
                onClick={() => onChange(value === o ? '' : o)}>{o}</button>
        ))}
    </div>
);

const fmtSize = (bytes) => (bytes / (1024 * 1024)).toFixed(1) + ' MB';

const FileUpload = ({ id, label, hint, file, onChange, onClear, error }) => (
    <div className="lvr__f">
        <label htmlFor={id}>{label}</label>
        <div className="lvr__upload">
            {file ? (
                <div className="lvr__filechip">
                    <div className="lvr__fileicon">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" stroke={BLUE} strokeWidth="1.8" strokeLinejoin="round" />
                            <path d="M14 3v5h5" stroke={BLUE} strokeWidth="1.8" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div className="lvr__filemeta">
                        <div className="lvr__filename">{file.name}</div>
                        <div className="lvr__filesize">{fmtSize(file.size)}</div>
                    </div>
                    <button type="button" className="lvr__fileremove" onClick={onClear} aria-label={`Remove ${file.name}`}>×</button>
                </div>
            ) : (
                <label htmlFor={id} className="lvr__uploadbtn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M12 16V4M12 4l-4 4M12 4l4 4" stroke={BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M4 16v3a2 2 0 002 2h12a2 2 0 002-2v-3" stroke={BLUE} strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Choose a file
                </label>
            )}
            <input id={id} className="lvr__uploadinput" type="file" onChange={onChange} />
            {hint && <div className="lvr__filenote">{hint}</div>}
            {error && <div className="lvr__filenote" style={{ color: PINK }}>{error}</div>}
        </div>
    </div>
);

export default function LowVoltageRequest() {
    const [projectName, setProjectName] = useState('');
    const [address, setAddress] = useState('');
    const [units, setUnits] = useState('');
    const [buildings, setBuildings] = useState('');
    const [projectType, setProjectType] = useState('');
    const [buildingType, setBuildingType] = useState('');
    const [isAffordable, setIsAffordable] = useState('');
    const [allocatingAgency, setAllocatingAgency] = useState('');
    const [roughIn, setRoughIn] = useState('');
    const [isBid, setIsBid] = useState('');
    const [bidDue, setBidDue] = useState('');
    const [systems, setSystems] = useState([]);
    const [ispStatus, setIspStatus] = useState('');
    const [msaDiscussed, setMsaDiscussed] = useState('');
    const [ispNotes, setIspNotes] = useState('');
    const [gc, setGc] = useState('');
    const [electrical, setElectrical] = useState('');
    const [budget, setBudget] = useState('');
    const [prevailingWage, setPrevailingWage] = useState('');
    const [name, setName] = useState('');
    const [company, setCompany] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [notes, setNotes] = useState('');
    const [files, setFiles] = useState({ sitePlan: null, ispAgreement: null, archPlans: null, elecPlans: null });
    const [fileErrors, setFileErrors] = useState({});
    const [status, setStatus] = useState('idle'); // idle | sending | done | error

    const toggleSystem = (s) => setSystems((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

    const pickFile = (key) => (e) => {
        const f = e.target.files && e.target.files[0];
        if (f && f.size > MAX_FILE_MB * 1024 * 1024) {
            setFileErrors((fe) => ({ ...fe, [key]: `That file is over ${MAX_FILE_MB}MB — email it separately once you submit.` }));
            setFiles((fs) => ({ ...fs, [key]: null }));
            e.target.value = '';
            return;
        }
        setFileErrors((fe) => ({ ...fe, [key]: null }));
        setFiles((fs) => ({ ...fs, [key]: f || null }));
    };
    const clearFile = (key) => setFiles((fs) => ({ ...fs, [key]: null }));

    const summaryText = () =>
        `PROJECT\nName: ${projectName || '—'}\nAddress: ${address || '—'}\nUnits: ${units || '—'}  Buildings/stories: ${buildings || '—'}\n` +
        `Project type: ${projectType || '—'}\nBuilding type: ${buildingType || '—'}\nAffordable housing (LIHTC): ${isAffordable || '—'}` +
        (isAffordable === 'Yes' ? `  (agency/round: ${allocatingAgency || '—'})` : '') +
        `\nTarget rough-in: ${roughIn || '—'}\nCompetitive bid: ${isBid || '—'}` + (isBid === 'Yes' ? `  (due: ${bidDue || '—'})` : '') +
        `\n\nSCOPE OF SYSTEMS\n${systems.join(', ') || '—'}\n\n` +
        `ISP & MARKETING AGREEMENT\nISP status: ${ispStatus || '—'}\nMSA / bulk-exclusive agreement discussed: ${msaDiscussed || '—'}\n` +
        `ISP agreement attached: ${files.ispAgreement ? files.ispAgreement.name : 'no'}\nNotes: ${ispNotes || '—'}\n\n` +
        `COORDINATION\nGC: ${gc || '—'}\nElectrical contractor: ${electrical || '—'}\nBudget: ${budget || '—'}\nPrevailing wage / Section 3: ${prevailingWage || '—'}\n\n` +
        `DOCUMENTS ATTACHED\nSite plan: ${files.sitePlan ? files.sitePlan.name : 'NOT ATTACHED'}\nISP agreement: ${files.ispAgreement ? files.ispAgreement.name : 'no'}\n` +
        `Architectural plans: ${files.archPlans ? files.archPlans.name : 'no'}\nElectrical plans: ${files.elecPlans ? files.elecPlans.name : 'no'}\n\n` +
        `CONTACT\n${name} — ${company}\n${email}${phone ? '  ·  ' + phone : ''}\n\nNotes: ${notes || '—'}`;

    const submit = async (e) => {
        e.preventDefault();
        if (!projectName || !address || !units || !name || !email) return;
        setStatus('sending');
        const fd = new FormData();
        fd.append('access_key', WEB3FORMS_ACCESS_KEY);
        fd.append('subject', `Low-Voltage Project Request — ${projectName} (${units} units)`);
        fd.append('from_name', 'BuildLab Low-Voltage Request');
        fd.append('name', name);
        fd.append('email', email);
        fd.append('company', company);
        fd.append('message', summaryText());
        if (files.sitePlan) fd.append('attachment', files.sitePlan);
        if (files.ispAgreement) fd.append('attachment2', files.ispAgreement);
        if (files.archPlans) fd.append('attachment3', files.archPlans);
        if (files.elecPlans) fd.append('attachment4', files.elecPlans);
        try {
            const res = await fetch('https://api.web3forms.com/submit', { method: 'POST', body: fd });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.success) setStatus('done');
            else setStatus('error');
        } catch (_) {
            setStatus('error');
        }
    };

    if (status === 'done') {
        return (
            <div className="lvr">
                <style dangerouslySetInnerHTML={{ __html: css }} />
                <div className="lvr__done">
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="11" fill="#E7F6EE" />
                        <path d="M7 12.5l3.2 3.2L17 9" stroke="#1F9D57" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <h3>Request received.</h3>
                    <p>Thanks, {name.split(' ')[0] || 'there'}. We'll review the site plan and scope, and follow up with next steps — including a look at any ISP agreement you attached — within one business day.</p>
                </div>
            </div>
        );
    }

    return (
        <form className="lvr" onSubmit={submit}>
            <style dangerouslySetInnerHTML={{ __html: css }} />

            <div className="lvr__sec">
                <div className="lvr__sh"><span className="lvr__st">Project basics</span></div>
                <div className="lvr__grid">
                    <Field label="Project name" required><input type="text" value={projectName} onChange={(e) => setProjectName(e.target.value)} required /></Field>
                    <Field label="Site address" required><input type="text" value={address} onChange={(e) => setAddress(e.target.value)} required /></Field>
                    <Field label="Number of units" required><input type="text" inputMode="numeric" value={units} onChange={(e) => setUnits(e.target.value)} required /></Field>
                    <Field label="Number of buildings / stories"><input type="text" placeholder="e.g. 2 buildings / 4 stories" value={buildings} onChange={(e) => setBuildings(e.target.value)} /></Field>
                </div>
                <Field label="Project type"><Segmented options={PROJECT_TYPES} value={projectType} onChange={setProjectType} /></Field>
                <Field label="Building type"><Chips options={BUILDING_TYPES} value={buildingType} onToggle={setBuildingType} /></Field>
                <Field label="Is this an affordable housing (LIHTC) project?"><Segmented options={YES_NO_UNSURE} value={isAffordable} onChange={setIsAffordable} /></Field>
                {isAffordable === 'Yes' && (
                    <Field label="Allocating agency / round (if known)"><input type="text" placeholder="e.g. TCAC 2026 9% round" value={allocatingAgency} onChange={(e) => setAllocatingAgency(e.target.value)} /></Field>
                )}
                <div className="lvr__grid">
                    <Field label="Target low-voltage rough-in"><input type="text" placeholder="e.g. Q2 2027" value={roughIn} onChange={(e) => setRoughIn(e.target.value)} /></Field>
                    <Field label="Is this a competitive bid?"><Segmented options={YES_NO} value={isBid} onChange={setIsBid} /></Field>
                </div>
                {isBid === 'Yes' && <Field label="Proposal due date"><input type="text" placeholder="e.g. 08/15/2026" value={bidDue} onChange={(e) => setBidDue(e.target.value)} /></Field>}
            </div>

            <div className="lvr__sec">
                <div className="lvr__sh"><span className="lvr__st">Scope of systems</span><span className="lvr__sn">select all that apply</span></div>
                <Chips options={SYSTEMS} value={systems} onToggle={toggleSystem} multi />
            </div>

            <div className="lvr__sec">
                <div className="lvr__sh"><span className="lvr__st">ISP & marketing agreements</span></div>
                <div className="lvr__warn">
                    <div className="lvr__warnicon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M12 2l8 4v6c0 5-3.4 8.4-8 10-4.6-1.6-8-5-8-10V6l8-4z" fill="#fff" />
                            <path d="M12 8v5M12 16.5v.01" stroke="#B45309" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </div>
                    <div>
                        <p className="lvr__warnt">Don't sign an ISP agreement before we look at it.</p>
                        <p className="lvr__warnd">
                            MDU broadband deals often hand the ISP exclusive marketing rights to residents while quietly
                            shifting conduit, IDF/MDF room build-out, power, and ongoing maintenance costs onto you.
                            We review the marketing services agreement (MSA) and confirm <b>every</b> infrastructure cost is
                            clearly assigned — so nothing gets left for you to cover after signing.
                        </p>
                    </div>
                </div>
                <Field label="ISP status for this property"><Segmented options={ISP_STATUS} value={ispStatus} onChange={setIspStatus} /></Field>
                <Field label="Has a marketing services / bulk-exclusive agreement been discussed?"><Segmented options={YES_NO_UNSURE} value={msaDiscussed} onChange={setMsaDiscussed} /></Field>
                <FileUpload id="lvr-isp-agreement" label="Upload the ISP agreement or term sheet, if you have one" hint={`Optional — PDF or Word, up to ${MAX_FILE_MB}MB.`} file={files.ispAgreement} onChange={pickFile('ispAgreement')} onClear={() => clearFile('ispAgreement')} error={fileErrors.ispAgreement} />
                <Field label="Anything specific you want us to check in the agreement?"><textarea rows={2} value={ispNotes} onChange={(e) => setIspNotes(e.target.value)} placeholder="Optional" /></Field>
            </div>

            <div className="lvr__sec">
                <div className="lvr__sh"><span className="lvr__st">Coordination & budget</span></div>
                <div className="lvr__grid">
                    <Field label="General contractor"><input type="text" value={gc} onChange={(e) => setGc(e.target.value)} /></Field>
                    <Field label="Electrical contractor"><input type="text" value={electrical} onChange={(e) => setElectrical(e.target.value)} /></Field>
                </div>
                <Field label="Approximate low-voltage budget"><Chips options={BUDGET_RANGES} value={budget} onToggle={setBudget} /></Field>
                <Field label="Prevailing wage / Section 3 requirements?"><Segmented options={YES_NO_UNSURE} value={prevailingWage} onChange={setPrevailingWage} /></Field>
            </div>

            <div className="lvr__sec">
                <div className="lvr__sh"><span className="lvr__st">Documents</span></div>
                <div className="lvr__hint">A site plan is what lets us actually review the property and put together a design — please attach one if you have it.</div>
                <FileUpload id="lvr-site-plan" label="Site plan" hint={`So we can review the property and provide a design. PDF, image, or CAD export — up to ${MAX_FILE_MB}MB.`} file={files.sitePlan} onChange={pickFile('sitePlan')} onClear={() => clearFile('sitePlan')} error={fileErrors.sitePlan} />
                <FileUpload id="lvr-arch-plans" label="Architectural plans (optional)" hint={`Floor plans, DD or CD level if available — up to ${MAX_FILE_MB}MB.`} file={files.archPlans} onChange={pickFile('archPlans')} onClear={() => clearFile('archPlans')} error={fileErrors.archPlans} />
                <FileUpload id="lvr-elec-plans" label="Electrical plans (optional)" hint={`Panel locations, conduit stub-ups — up to ${MAX_FILE_MB}MB.`} file={files.elecPlans} onChange={pickFile('elecPlans')} onClear={() => clearFile('elecPlans')} error={fileErrors.elecPlans} />
            </div>

            <div className="lvr__sec">
                <div className="lvr__sh"><span className="lvr__st">Where do we send the proposal?</span></div>
                <div className="lvr__grid">
                    <Field label="Your name" required><input type="text" value={name} onChange={(e) => setName(e.target.value)} required /></Field>
                    <Field label="Company" required><input type="text" value={company} onChange={(e) => setCompany(e.target.value)} required /></Field>
                    <Field label="Email" required><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
                    <Field label="Phone"><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
                </div>
                <Field label="Anything else we should know?"><textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" /></Field>
            </div>

            <button className="lvr__btn" type="submit" disabled={status === 'sending' || !projectName || !address || !units || !name || !email}>
                {status === 'sending' ? 'Sending…' : 'Send project request →'}
            </button>
            <div className="lvr__disc">We'll follow up within one business day. No obligation.</div>
            {status === 'error' && (
                <div className="lvr__msg">
                    Couldn't send just now — files can't travel over email links, so please email your details and attachments directly to{' '}
                    <a style={{ color: BLUE, fontWeight: 700 }} href={`mailto:${FALLBACK_EMAIL}?subject=${encodeURIComponent('Low-Voltage Project Request — ' + (projectName || ''))}&body=${encodeURIComponent(summaryText())}`}>{FALLBACK_EMAIL}</a>.
                </div>
            )}
        </form>
    );
}
